// YMM Widget — Shopify storefront client. v2026-10-15
// Calls the ERP via the App Proxy mounted at /apps/ymm/*.
//
// Public API on `window.YMM`:
//   YMM.init()                       — wire up the YMM bar
//   YMM.renderBadge(el, listingId)   — render a fits/doesn't-fit badge into el
//   YMM.openGapForm(prefill)         — open the "don't see your bike" form

(function (window, document) {
  'use strict'

  const COOKIE_NAME  = 'ymm_bike'
  const COOKIE_DAYS  = 365
  const PROXY_BASE   = '/apps/ymm'
  const GARAGE_KEY   = 'ymm_garage'

  // ── State ────────────────────────────────────────────────
  let currentBike = null
  let makesCache  = []
  const modelsCacheByMakeId = new Map()
  const yearsCacheByModelId = new Map()

  // Garage state
  let garageBikes   = []
  let garageMerged  = false

  // UI refs — set in init()
  let _panel          = null   // unified selector panel
  let _stripLabel     = null   // bike name span in the header strip
  let _stripDot       = null   // status dot in the header strip
  let _inlineGarageDot = null  // green dot on desktop garage button
  let _garageList     = null   // garage list inside the selector panel
  let _gapPanel       = null
  let _backdrop       = null
  let _makeEl         = null   // Make select inside the panel (used by switchActiveBike)
  let _modelEl        = null
  let _yearEl         = null

  // Legacy refs for openGapForm public API (set in init)
  let gapFormElRef     = null
  let gapHeadlineElRef = null

  // ── Cookie helpers ───────────────────────────────────────

  function setCookie(name, value, days) {
    const d = new Date()
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`
  }

  function getCookie(name) {
    const prefix = name + '='
    for (const part of document.cookie.split(';')) {
      const t = part.trim()
      if (t.indexOf(prefix) === 0) return decodeURIComponent(t.substring(prefix.length))
    }
    return null
  }

  // On a Shopify PDP a bike change should update the fits badge in place, not
  // bounce to /pages/ymm-results. Detect by URL — Shopify PDPs live at
  // /products/<handle> (with an optional /collections/<handle> prefix).
  function isProductPage() {
    return /\/products\/[^/]+/.test(window.location.pathname)
  }

  // ── Fetch helpers ────────────────────────────────────────

  if (!window.__ymmCache__) {
    window.__ymmCache__ = (function () {
      const TTL_MS  = 30 * 60 * 1000
      const inflight = {}
      function rd(k) {
        try {
          const raw = sessionStorage.getItem(k)
          if (!raw) return null
          const o = JSON.parse(raw)
          if (Date.now() - o.t > TTL_MS) return null
          return o.v
        } catch { return null }
      }
      function wr(k, v) {
        try { sessionStorage.setItem(k, JSON.stringify({ t: Date.now(), v })) } catch {}
      }
      return function (url, key) {
        const hit = rd(key)
        if (hit) return Promise.resolve(hit)
        if (inflight[key]) return inflight[key]
        inflight[key] = fetch(url, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .then((v) => { if (v) wr(key, v); delete inflight[key]; return v })
          .catch((e) => { delete inflight[key]; throw e })
        return inflight[key]
      }
    })()
  }

  const CACHED_GET_PATHS = { '/makes': 1, '/models': 1, '/years': 1 }

  function get(path, params) {
    const url = new URL(PROXY_BASE + path, window.location.origin)
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== null && v !== undefined && v !== '') url.searchParams.set(k, v)
      }
    }
    if (CACHED_GET_PATHS[path]) {
      const parts = []
      if (params) {
        const keys = Object.keys(params).sort()
        for (const k of keys) {
          if (params[k] !== null && params[k] !== undefined && params[k] !== '')
            parts.push(k + '=' + params[k])
        }
      }
      return window.__ymmCache__(
        url.toString(),
        'ymm:' + path + (parts.length ? ':' + parts.join('&') : ''),
      )
    }
    return fetch(url.toString(), { credentials: 'same-origin' }).then((r) => r.json())
  }

  function post(path, body) {
    return fetch(PROXY_BASE + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body || {}),
    }).then((r) => r.json())
  }

  // ── Bike persistence ─────────────────────────────────────

  function loadBikeFromCookie() {
    const raw = getCookie(COOKIE_NAME)
    if (!raw) return null
    try { return JSON.parse(raw) } catch { return null }
  }

  function saveBike(bike) {
    currentBike = bike
    setCookie(COOKIE_NAME, JSON.stringify(bike), COOKIE_DAYS)
    document.querySelectorAll('[data-ymm-badge]').forEach((el) => {
      const lid = el.getAttribute('data-listing-id')
      if (lid) renderBadge(el, lid)
    })
    onBikeAdded(bike)
  }

  function persistBikeToServer(bike) {
    if (!window.YMM_CUSTOMER_ID) return Promise.resolve()
    const p = fetch(PROXY_BASE + '/me/bike', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ year_id: bike.year_id, make_id: bike.make_id, model_id: bike.model_id }),
    }).catch(() => {})
    const timeout = new Promise((resolve) => setTimeout(resolve, 3000))
    return Promise.race([p, timeout])
  }

  // ── Select helpers ───────────────────────────────────────

  function fillSelect(selectEl, items, valueKey, labelKey) {
    const placeholder = selectEl.options[0]
    selectEl.innerHTML = ''
    if (placeholder) selectEl.appendChild(placeholder)
    for (const item of items) {
      const opt = document.createElement('option')
      opt.value       = String(item[valueKey])
      opt.textContent = String(item[labelKey])
      selectEl.appendChild(opt)
    }
  }

  // ── Selector panel open / close ──────────────────────────

  function openPanel() {
    if (!_panel) return
    _panel.classList.add('ymm--open')
    if (_backdrop) _backdrop.classList.add('ymm--open')
    document.body.style.overflow = 'hidden'
  }

  function closePanel() {
    if (!_panel) return
    _panel.classList.remove('ymm--open')
    if (_backdrop) _backdrop.classList.remove('ymm--open')
    document.body.style.overflow = ''
  }

  function openGapPanel(prefill) {
    if (!_gapPanel) return
    _gapPanel.dataset.surface            = (prefill && prefill.surface)            || 'manual_link'
    _gapPanel.dataset.requestedListingId = (prefill && prefill.requested_listing_id) || ''
    if (prefill) {
      const y  = _gapPanel.querySelector('[name="year"]')
      const mk = _gapPanel.querySelector('[name="make"]')
      const md = _gapPanel.querySelector('[name="model"]')
      if (y  && prefill.year)  y.value  = prefill.year
      if (mk && prefill.make)  mk.value = prefill.make
      if (md && prefill.model) md.value = prefill.model
    }
    if (gapHeadlineElRef && prefill && prefill.headline) {
      gapHeadlineElRef.textContent = prefill.headline
    }
    _gapPanel.classList.add('ymm--open')
    if (_backdrop) _backdrop.classList.add('ymm--open')
  }

  function closeGapPanel() {
    if (!_gapPanel) return
    _gapPanel.classList.remove('ymm--open')
  }

  function openGapForm(prefill) {
    openGapPanel(prefill)
  }

  // ── Active-bike label ────────────────────────────────────

  function updateActiveLabel(bike) {
    const text = bike
      ? String(bike.year) + ' ' + bike.make + ' ' + bike.model
      : 'Select your bike'
    if (_stripLabel) {
      _stripLabel.textContent = text
      _stripLabel.classList.toggle('ymm-strip-bike--empty', !bike)
    }
    if (_stripDot) _stripDot.classList.toggle('ymm-strip-dot--on', !!bike)
    if (_inlineGarageDot) _inlineGarageDot.classList.toggle('ymm-inline-garage-dot--on', !!bike)
  }

  // ── Garage list render ───────────────────────────────────

  function buildGarageItems(bikes, onSwitch, onClose) {
    const frag = document.createDocumentFragment()
    if (!bikes || bikes.length === 0) {
      const empty = document.createElement('li')
      empty.style.cssText =
        'padding:.75rem .85rem;font-size:12px;color:rgba(255,255,255,.3);text-align:center;'
      empty.textContent = 'No bikes saved yet'
      frag.appendChild(empty)
      return frag
    }
    bikes.forEach(function (bike, i) {
      const isPrimary = i === 0
      const li        = document.createElement('li')
      li.className    = 'ymm-garage-row' + (isPrimary ? ' ymm--primary' : '')

      const dot = document.createElement('span')
      dot.className = 'ymm-row-dot'
      li.appendChild(dot)

      const label = document.createElement('span')
      label.className   = 'ymm-row-label'
      label.textContent = String(bike.year) + ' ' + bike.make + ' ' + bike.model
      li.appendChild(label)

      const sel       = document.createElement('button')
      sel.type        = 'button'
      sel.className   = 'ymm-row-select'
      sel.textContent = 'Select'
      sel.addEventListener('click', function (e) {
        e.stopPropagation()
        switchActiveBike(bike.id, i)
      })
      li.appendChild(sel)
      li.addEventListener('click', function () { switchActiveBike(bike.id, i) })

      const del = document.createElement('button')
      del.type      = 'button'
      del.className = 'ymm-row-del'
      del.innerHTML = '&times;'
      del.setAttribute('aria-label', 'Remove ' + label.textContent)
      del.addEventListener('click', function (e) { e.stopPropagation(); removeBike(bike.id, i) })
      li.appendChild(del)

      frag.appendChild(li)
    })
    return frag
  }

  function renderGaragePanel(bikes) {
    if (_garageList) {
      _garageList.innerHTML = ''
      _garageList.appendChild(buildGarageItems(bikes, null, closePanel))
    }
  }

  // ── Pre-fill the three selects ───────────────────────────

  function setFieldHasValue(selectEl, hasValue) {
    const wrapper = selectEl && selectEl.closest('.rh-ymm__field')
    if (wrapper) wrapper.classList.toggle('has-value', hasValue)
  }

  async function prefillSelects(bike, makeEl, modelEl, yearEl) {
    if (!bike || !bike.make_id) return

    makeEl.value = bike.make_id
    if (makeEl.value !== bike.make_id) return
    setFieldHasValue(makeEl, !!makeEl.value)

    let models = modelsCacheByMakeId.get(bike.make_id)
    if (!models) {
      try {
        const r = await get('/models', { make_id: bike.make_id })
        models  = (r && r.models) || []
        modelsCacheByMakeId.set(bike.make_id, models)
      } catch { return }
    }
    fillSelect(modelEl, models, 'id', 'name')
    modelEl.disabled = false
    modelEl.value    = bike.model_id
    if (!modelEl.value || modelEl.value !== bike.model_id) return
    setFieldHasValue(modelEl, !!modelEl.value)

    let years = yearsCacheByModelId.get(bike.model_id)
    if (!years) {
      try {
        const r = await get('/years', { model_id: bike.model_id })
        years   = (r && r.years) || []
        yearsCacheByModelId.set(bike.model_id, years)
      } catch { return }
    }
    fillSelect(yearEl, years, 'id', 'year')
    yearEl.disabled = false
    yearEl.value    = bike.year_id
    setFieldHasValue(yearEl, !!yearEl.value)
  }

  // ── Search + navigate ────────────────────────────────────

  async function onAllThreeFilled(makeEl, modelEl, yearEl, currentLabel) {
    const makeId  = makeEl.value
    const modelId = modelEl.value
    const yearId  = yearEl.value
    if (!makeId || !modelId || !yearId) return

    const matchedMake  = makesCache.find((m) => m.id === makeId) || null
    const models       = modelsCacheByMakeId.get(makeId) || []
    const matchedModel = models.find((m) => m.id === modelId) || null
    const years        = yearsCacheByModelId.get(modelId) || []
    const matchedYear  = years.find((y) => y.id === yearId) || null
    if (!matchedMake || !matchedModel || !matchedYear) return

    const bike = {
      year_id:  yearId,  make_id:  makeId,  model_id: modelId,
      year:     matchedYear.year, make: matchedMake.name, model: matchedModel.name,
    }

    // PDP fast path: keep the user on the product, refresh the fits badge in
    // place. The badge listens to ymm:bike-changed for re-check + auto-variant
    // switch. Skip /search + the gap-panel-on-zero-results flow + navigation.
    if (isProductPage()) {
      saveBike(bike)
      if (currentLabel) currentLabel.textContent = bike.year + ' ' + bike.make + ' ' + bike.model
      persistBikeToServer(bike)
      document.dispatchEvent(new CustomEvent('ymm:bike-changed', { detail: bike, bubbles: false }))
      closePanel()
      return
    }

    let count = 0, searchError = false, searchResult = null
    try {
      searchResult = await post('/search', { year_id: yearId, make_id: makeId, model_id: modelId })
      if (searchResult && searchResult.error) {
        searchError = true
      } else {
        count = searchResult && typeof searchResult.count === 'number'
          ? searchResult.count
          : ((searchResult && searchResult.listings && searchResult.listings.length) || 0)
      }
    } catch { searchError = true }

    if (searchError) return

    try {
      sessionStorage.setItem('ymm_search_cache', JSON.stringify({
        year_id: yearId, make_id: makeId, model_id: modelId,
        results: searchResult, ts: Date.now(),
      }))
    } catch {}

    if (count === 0) {
      openGapPanel({
        surface:  'header_no_results',
        year:     String(matchedYear.year),
        make:     matchedMake.name,
        model:    matchedModel.name,
        headline: "We don't have parts for that bike yet — tell us what you'd buy and we'll prioritize it.",
      })
      return
    }

    saveBike(bike)
    if (currentLabel) currentLabel.textContent = bike.year + ' ' + bike.make + ' ' + bike.model
    persistBikeToServer(bike) // fire-and-forget — don't block navigation

    window.location.href = '/pages/ymm-results'
      + '?year_id='  + encodeURIComponent(yearId)
      + '&make_id='  + encodeURIComponent(makeId)
      + '&model_id=' + encodeURIComponent(modelId)
      + '&year='     + encodeURIComponent(String(matchedYear.year))
      + '&make='     + encodeURIComponent(matchedMake.name)
      + '&model='    + encodeURIComponent(matchedModel.name)
  }

  // ── Search + navigate for a fully-resolved bike object ───

  async function searchAndNavigate(bike) {
    let count = 0, searchError = false, searchResult = null
    try {
      searchResult = await post('/search', { year_id: bike.year_id, make_id: bike.make_id, model_id: bike.model_id })
      if (searchResult && searchResult.error) {
        searchError = true
      } else {
        count = searchResult && typeof searchResult.count === 'number'
          ? searchResult.count
          : ((searchResult && searchResult.listings && searchResult.listings.length) || 0)
      }
    } catch { searchError = true }

    if (searchError) return

    try {
      sessionStorage.setItem('ymm_search_cache', JSON.stringify({
        year_id: bike.year_id, make_id: bike.make_id, model_id: bike.model_id,
        results: searchResult, ts: Date.now(),
      }))
    } catch {}

    if (count === 0) {
      openGapPanel({
        surface:  'header_no_results',
        year:     String(bike.year),
        make:     bike.make,
        model:    bike.model,
        headline: "We don't have parts for that bike yet — tell us what you'd buy and we'll prioritize it.",
      })
      return
    }

    window.location.href = '/pages/ymm-results'
      + '?year_id='  + encodeURIComponent(bike.year_id)
      + '&make_id='  + encodeURIComponent(bike.make_id)
      + '&model_id=' + encodeURIComponent(bike.model_id)
      + '&year='     + encodeURIComponent(String(bike.year))
      + '&make='     + encodeURIComponent(bike.make)
      + '&model='    + encodeURIComponent(bike.model)
  }

  // ── Garage localStorage ──────────────────────────────────

  function loadGarageFromStorage() {
    try {
      const raw = localStorage.getItem(GARAGE_KEY)
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch { return [] }
  }

  function saveGarageToStorage(bikes) {
    try { localStorage.setItem(GARAGE_KEY, JSON.stringify(bikes.slice(0, 10))) } catch {}
  }

  // ── Bike added (called after saveBike) ───────────────────

  function onBikeAdded(bike) {
    updateActiveLabel(bike)
    if (window.YMM_CUSTOMER_ID) {
      const already = garageBikes.some(
        (b) => b.make_id === bike.make_id && b.model_id === bike.model_id && b.year_id === bike.year_id,
      )
      if (!already) {
        garageBikes = [bike, ...garageBikes]
      } else {
        garageBikes = [
          bike,
          ...garageBikes.filter(
            (b) => !(b.make_id === bike.make_id && b.model_id === bike.model_id && b.year_id === bike.year_id),
          ),
        ]
      }
      renderGaragePanel(garageBikes)
      return
    }
    const existing = loadGarageFromStorage()
    const already  = existing.some(
      (b) => b.make_id === bike.make_id && b.model_id === bike.model_id && b.year_id === bike.year_id,
    )
    const updated = already
      ? [bike, ...existing.filter(
          (b) => !(b.make_id === bike.make_id && b.model_id === bike.model_id && b.year_id === bike.year_id),
        )]
      : [bike, ...existing]
    saveGarageToStorage(updated)
    garageBikes = updated
    renderGaragePanel(garageBikes)
  }

  // ── Garage cookie + event ────────────────────────────────

  function garageSetActiveCookie(bike) {
    const cb = {
      year_id: bike.year_id, make_id: bike.make_id, model_id: bike.model_id,
      year: bike.year, make: bike.make, model: bike.model,
    }
    currentBike = cb
    setCookie(COOKIE_NAME, JSON.stringify(cb), COOKIE_DAYS)
    updateActiveLabel(cb)
    document.dispatchEvent(new CustomEvent('ymm:bike-changed', { detail: cb, bubbles: false }))
    document.querySelectorAll('[data-ymm-badge]').forEach((el) => {
      const lid = el.getAttribute('data-listing-id')
      if (lid) renderBadge(el, lid)
    })
  }

  // ── Switch active bike ───────────────────────────────────

  async function switchActiveBike(bikeId, bikeIndex) {
    // Selecting a garage bike just updates the active bike + dispatches
    // ymm:bike-changed (via garageSetActiveCookie). Badges re-check in place;
    // we never navigate to /pages/ymm-results from the garage anymore.
    if (window.YMM_CUSTOMER_ID) {
      try {
        const r = await post('/me/garage/switch', { bike_id: bikeId })
        if (!r || !r.ok) return
        if (r.bike) {
          const b    = r.bike
          const bike = {
            year_id: b.ids.year_id, make_id: b.ids.make_id, model_id: b.ids.model_id,
            year: b.year, make: b.make, model: b.model,
          }
          garageSetActiveCookie(bike)
          if (_makeEl) prefillSelects(bike, _makeEl, _modelEl, _yearEl)
        }
        await loadGarageFromServer(false)
      } catch (err) {
        console.error('[YMM garage] switchActiveBike error:', err)
      }
    } else {
      const idx = bikeIndex != null ? bikeIndex : garageBikes.findIndex((b) => b.id === bikeId)
      if (idx < 0) return
      const selected = garageBikes[idx]
      if (idx > 0) {
        const updated = [selected, ...garageBikes.slice(0, idx), ...garageBikes.slice(idx + 1)]
        saveGarageToStorage(updated)
        garageBikes = updated
        renderGaragePanel(garageBikes)
      }
      garageSetActiveCookie(selected)
      if (_makeEl) prefillSelects(selected, _makeEl, _modelEl, _yearEl)
    }
  }

  // ── Remove bike ──────────────────────────────────────────

  async function removeBike(bikeId, bikeIndex) {
    const idx = bikeIndex != null ? Number(bikeIndex) : garageBikes.findIndex((b) => b.id === bikeId)
    if (idx < 0) return
    const wasPrimary = idx === 0

    if (window.YMM_CUSTOMER_ID) {
      try {
        const url = PROXY_BASE + '/me/garage?bike_id=' + encodeURIComponent(bikeId)
        const r   = await fetch(url, { method: 'DELETE', credentials: 'same-origin' }).then((res) => res.json())
        if (!r || !r.ok) return
        await loadGarageFromServer(false)
        if (wasPrimary) {
          if (garageBikes.length > 0) garageSetActiveCookie(garageBikes[0])
          else { currentBike = null; setCookie(COOKIE_NAME, '', -1); updateActiveLabel(null) }
        }
      } catch (err) {
        console.error('[YMM garage] removeBike error:', err)
      }
    } else {
      const updated = [...garageBikes.slice(0, idx), ...garageBikes.slice(idx + 1)]
      saveGarageToStorage(updated)
      garageBikes = updated
      if (wasPrimary) {
        if (updated.length > 0) garageSetActiveCookie(updated[0])
        else { currentBike = null; setCookie(COOKIE_NAME, '', -1); updateActiveLabel(null) }
      }
      renderGaragePanel(garageBikes)
    }
  }

  // ── Load garage from server ──────────────────────────────

  async function loadGarageFromServer(isFirstLoad) {
    try {
      const r = await get('/me/garage')
      if (!r || !Array.isArray(r.bikes)) return
      if (isFirstLoad) mergeLocalStorageGarage(r.bikes)
      garageBikes = r.bikes
      // Sync the label: server primary is index 0
      if (garageBikes.length > 0) {
        const primary = garageBikes[0]
        if (!currentBike) garageSetActiveCookie(primary)
        else updateActiveLabel(currentBike)
      }
      renderGaragePanel(garageBikes)
    } catch (err) {
      console.error('[YMM garage] loadGarageFromServer error:', err)
    }
  }

  async function mergeLocalStorageGarage(serverBikes) {
    if (garageMerged) return
    garageMerged = true
    const local = loadGarageFromStorage()
    if (!local || local.length === 0) return
    const serverSet = new Set(serverBikes.map((b) => `${b.make_id}|${b.model_id}|${b.year_id}`))
    const toMerge   = local.filter(
      (b) => b.make_id && b.model_id && b.year_id && !serverSet.has(`${b.make_id}|${b.model_id}|${b.year_id}`),
    )
    if (toMerge.length === 0) { saveGarageToStorage([]); return }
    try {
      await post('/me/garage/merge', {
        bikes: toMerge.map((b) => ({ make_id: b.make_id, model_id: b.model_id, year_id: b.year_id })),
      })
      saveGarageToStorage([])
    } catch (err) {
      console.error('[YMM garage] merge error (non-fatal):', err)
    }
  }

  // ── Fits badge ───────────────────────────────────────────

  async function renderBadge(el, listingId, opts) {
    const surface = (opts && opts.surface) || 'pdp'
    if (!currentBike) {
      el.textContent = 'Pick your bike to see fitment'
      el.className   = 'ymm-badge ymm-badge--unknown'
      return
    }
    try {
      const r = await get('/check', {
        listing_id: listingId,
        year_id:    currentBike.year_id,
        make_id:    currentBike.make_id,
        model_id:   currentBike.model_id,
        surface,
      })
      if (r.fits) {
        el.textContent = '✓ Fits your ' + currentBike.year + ' ' + currentBike.make + ' ' + currentBike.model
        el.className   = 'ymm-badge ymm-badge--fits'
      } else {
        el.textContent = '✗ Does not fit your ' + currentBike.year + ' ' + currentBike.make + ' ' + currentBike.model
        el.className   = 'ymm-badge ymm-badge--no-fits'
      }
    } catch {
      el.textContent = ''
    }
  }

  // ── Init ─────────────────────────────────────────────────

  async function init() {
    const root = document.querySelector('[data-ymm-selector]')
    if (!root) return

    // ── Pull elements from the Liquid ────────────────────
    const panel    = root.querySelector('[data-ymm-panel]')
    const gapPanel = root.querySelector('[data-ymm-gap-panel]')
    const backdrop = root.querySelector('[data-ymm-backdrop]')

    // Move floating panels to <body> so they escape the section's stacking context.
    // The sectionWrapper gets position:sticky + z-index which creates a new stacking
    // context — any fixed/high-z-index child is capped at that context's z level.
    // Moving them to body gives them full document-root z-index.
    if (panel)    document.body.appendChild(panel)
    if (gapPanel) document.body.appendChild(gapPanel)
    if (backdrop) document.body.appendChild(backdrop)

    // Store refs
    _panel      = panel
    _gapPanel   = gapPanel
    _backdrop   = backdrop
    _garageList = panel && panel.querySelector('[data-ymm-garage-list]')
    _stripLabel = root.querySelector('[data-ymm-strip-label]')
    _stripDot   = root.querySelector('[data-ymm-dot]')

    const makeEl    = panel && panel.querySelector('[data-ymm-make]')
    const modelEl   = panel && panel.querySelector('[data-ymm-model]')
    const yearEl    = panel && panel.querySelector('[data-ymm-year]')
    const searchBtn = panel && panel.querySelector('[data-ymm-search]')
    _makeEl = makeEl
    _modelEl = modelEl
    _yearEl  = yearEl

    gapFormElRef     = gapPanel
    gapHeadlineElRef = gapPanel && gapPanel.querySelector('[data-ymm-gap-headline]')

    const currentLabel = root.querySelector('[data-ymm-current]')

    var _strip = root.querySelector('.ymm-header-strip')

    // ── Weld the strip to the sticky header ──────────────────────────────
    // position:fixed + scroll-tracking always lags by one rAF because the
    // browser moves the header before JS can respond. The only way to be
    // truly in sync is to make the strip a child of the sticky-header element
    // so the browser treats them as one unit — no JS tracking needed.
    //
    // Fallback: if sticky-header isn't found, pin with position:fixed at the
    // header's measured height (static, better than nothing).
    function findStickyHeader() {
      return document.querySelector(
        'sticky-header, .site-header-sticky-wrapper, header.header, .site-header, header[role="banner"]'
      )
    }

    var stickyHdr = findStickyHeader()
    if (stickyHdr && _strip) {
      // Move strip into the sticky-header element — it now scrolls, sticks,
      // and hides exactly with the header, zero lag, no scroll listeners needed.
      stickyHdr.appendChild(_strip)
      // Clear any inline positioning left from previous renders.
      _strip.style.cssText = _strip.style.cssText
        .replace(/position[^;]*;?/gi, '')
        .replace(/top[^;]*;?/gi, '')
        .replace(/left[^;]*;?/gi, '')
        .replace(/right[^;]*;?/gi, '')
        .replace(/z-index[^;]*;?/gi, '')
        .replace(/transform[^;]*;?/gi, '')

      // After welding, the strip lives in sticky-header but our section
      // wrapper lost the in-flow space it used to occupy. On themes where
      // sticky-header is position:fixed (out of flow) or where the next
      // section uses a negative-margin bleed, page content slides up
      // under the strip. Detect this and reserve the strip's height in
      // our wrapper to push content back below it. Skip when the layout
      // already reflowed correctly so we don't create a phantom band.
      var reserveStripSpace = function () {
        if (!root || !_strip) return
        var stripRect = _strip.getBoundingClientRect()
        if (stripRect.height < 1) return
        // Clear any previous reservation so the measurement is honest.
        root.style.minHeight = ''
        var ymmSection = root.closest('[id^="shopify-section-"]')
        var probe = ymmSection ? ymmSection.nextElementSibling : null
        while (probe && probe.getBoundingClientRect().height < 1) {
          probe = probe.nextElementSibling
        }
        if (!probe) return
        var probeTop = probe.getBoundingClientRect().top
        // Allow a half-pixel of subpixel rounding before flagging overlap.
        if (probeTop < stripRect.bottom - 0.5) {
          root.style.minHeight = stripRect.height + 'px'
        }
      }
      requestAnimationFrame(reserveStripSpace)
      // Re-check on resize: strip height changes across breakpoints, and
      // theme re-layouts (font load, lazy images) can shift probe targets.
      var _resizeRaf = 0
      window.addEventListener('resize', function () {
        if (_resizeRaf) cancelAnimationFrame(_resizeRaf)
        _resizeRaf = requestAnimationFrame(reserveStripSpace)
      })
    } else {
      // Fallback — sticky-header not in DOM yet or not found.
      // position:fixed pulls the strip out of flow, so reserve its height on
      // the wrapper to keep page content from jumping up underneath it.
      if (_strip) {
        root.style.minHeight = _strip.getBoundingClientRect().height + 'px'
        var _h = stickyHdr ? stickyHdr.getBoundingClientRect().height : 0
        _strip.style.position = 'fixed'
        _strip.style.top      = _h + 'px'
        _strip.style.left     = '0'
        _strip.style.right    = '0'
        _strip.style.zIndex   = '29'
      }
    }

    // ── Desktop inline elements (found after strip move) ──────────
    // The strip was moved to stickyHdr above; querySelector still works via the ref.
    const inlineMakeEl   = _strip ? _strip.querySelector('[data-ymm-inline-make]')    : null
    const inlineModelEl  = _strip ? _strip.querySelector('[data-ymm-inline-model]')   : null
    const inlineYearEl   = _strip ? _strip.querySelector('[data-ymm-inline-year]')    : null
    const inlineFindBtn  = _strip ? _strip.querySelector('[data-ymm-inline-find]')    : null
    const inlineGarageBtn = _strip ? _strip.querySelector('[data-ymm-inline-garage]') : null
    _inlineGarageDot = _strip ? _strip.querySelector('[data-ymm-inline-dot]') : null

    // ── Wire every trigger before any network call ───────
    // Everything below up to "Load saved bike" is synchronous. It must run
    // before the /makes and /me/bike awaits: until then a tap on the header
    // strip or a "#ymm" CTA did nothing (and never would if the app proxy
    // hung). Selects fill in once /makes returns; the panel can open first.
    // ── Strip trigger → open selector panel ───────────────
    // Query from _strip, not root — strip was moved into stickyHdr so it's no longer a root descendant.
    const stripTrigger = (_strip || root).querySelector('[data-ymm-strip-trigger]')
    if (stripTrigger) stripTrigger.addEventListener('click', openPanel)

    // Desktop garage button — opens the same panel (shows garage list + selects)
    if (inlineGarageBtn) inlineGarageBtn.addEventListener('click', openPanel)

    // ── External triggers (home-page CTAs, RH Hero, custom sections) ──
    // Any <a href> ending in "#ymm" OR any element with [data-ymm-open]
    // opens the panel. Operators set Shopify button URLs to "#ymm" or "/#ymm".
    // Direct landings on "/#ymm" also open the panel (hash is stripped after).
    if (!document._ymmExternalTriggersWired) {
      document._ymmExternalTriggersWired = true

      document.addEventListener('click', function (e) {
        const el = e.target && e.target.closest && e.target.closest('a[href], [data-ymm-open]')
        if (!el) return
        const href = el.getAttribute('href') || ''
        if (el.hasAttribute('data-ymm-open') || /#(?:ymm|rh-ymm-bar|find-parts)$/i.test(href.trim())) {
          e.preventDefault()
          openPanel()
        }
      }, true)

      function checkOpenHash() {
        const h = (window.location.hash || '').toLowerCase()
        if (h === '#ymm' || h === '#rh-ymm-bar' || h === '#find-parts') {
          try { history.replaceState(null, '', window.location.pathname + window.location.search) } catch (_) {}
          openPanel()
        }
      }
      window.addEventListener('hashchange', checkOpenHash)
      checkOpenHash()
    }

    // ── Panel close ───────────────────────────────────────
    const panelClose = panel && panel.querySelector('[data-ymm-panel-close]')
    if (panelClose) panelClose.addEventListener('click', closePanel)

    // ── Gap panel trigger from inside selector panel ───────
    const gapTrigger = panel && panel.querySelector('[data-ymm-gap-trigger]')
    if (gapTrigger) gapTrigger.addEventListener('click', function () {
      openGapPanel({ surface: 'manual_link' })
    })

    // ── Gap panel controls ────────────────────────────────
    const gapClose = gapPanel && gapPanel.querySelector('[data-ymm-gap-close]')
    if (gapClose) gapClose.addEventListener('click', closeGapPanel)

    const gapBack = gapPanel && gapPanel.querySelector('[data-ymm-gap-back]')
    if (gapBack) {
      gapBack.addEventListener('click', function () {
        closeGapPanel()
        openPanel()
      })
    }

    const gapSend = gapPanel && gapPanel.querySelector('[data-ymm-gap-send]')
    if (gapSend && gapPanel) {
      gapSend.addEventListener('click', async function () {
        const body = {
          raw_year:             String((gapPanel.querySelector('[name="year"]')             || {}).value || ''),
          raw_make:             String((gapPanel.querySelector('[name="make"]')             || {}).value || ''),
          raw_model:            String((gapPanel.querySelector('[name="model"]')            || {}).value || ''),
          desired_product:      String((gapPanel.querySelector('[name="desired_product"]')  || {}).value || ''),
          customer_email:       String((gapPanel.querySelector('[name="email"]')            || {}).value || ''),
          notes:                String((gapPanel.querySelector('[name="notes"]')            || {}).value || ''),
          surface:              gapPanel.dataset.surface || 'manual_link',
          requested_listing_id: gapPanel.dataset.requestedListingId || '',
        }
        try {
          await post('/gap', body)
          if (gapHeadlineElRef)
            gapHeadlineElRef.textContent =
              'Got it — thanks. We log every request so we know which bikes and parts to prioritize.'
          gapPanel.querySelectorAll(
            '.ymm-gap-row,.ymm-gap-field,.ymm-gap-area,.ymm-gap-footer',
          ).forEach(function (el) { el.style.display = 'none' })
        } catch {
          if (gapHeadlineElRef)
            gapHeadlineElRef.textContent = 'Something went wrong. Please email us directly.'
        }
      })
    }

    // ── Backdrop (click-outside to close) ─────────────────
    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closePanel()
        closeGapPanel()
      })
    }

    // ── Nav-conflict guard ────────────────────────────────
    // Shopify themes signal a nav/drawer is open by scroll-locking the body.
    // Close our selector panel if a nav drawer opens to avoid overlap.
    ;(function () {
      var NAV_OPEN_RE = /\b(overflow-hidden|no-scroll|nav-open|menu-open|drawer-open|is-overlay-open|mobile-nav-open)\b/

      function syncPanel() {
        if (_panel && _panel.classList.contains('ymm--open')) return
        var bodyEl = document.body
        var htmlEl = document.documentElement
        var navOpen =
          bodyEl.style.overflow === 'hidden' ||
          htmlEl.style.overflow === 'hidden' ||
          NAV_OPEN_RE.test(bodyEl.className) ||
          NAV_OPEN_RE.test(htmlEl.className)
        if (navOpen) closePanel()
      }

      var mo = new MutationObserver(syncPanel)
      mo.observe(document.body,            { attributes: true, attributeFilter: ['class', 'style'] })
      mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] })
    })()

    // ── Load saved bike ──────────────────────────────────
    currentBike = loadBikeFromCookie()
    updateActiveLabel(currentBike)
    if (currentLabel && currentBike)
      currentLabel.textContent = currentBike.year + ' ' + currentBike.make + ' ' + currentBike.model

    // ── Load makes ──────────────────────────────────────
    if (makeEl || inlineMakeEl) {
      try {
        const makesData = await get('/makes')
        makesCache = (makesData && makesData.makes) || []
      } catch { makesCache = [] }
      if (makeEl) { fillSelect(makeEl, makesCache, 'id', 'name'); makeEl.disabled = false }
      if (inlineMakeEl) { fillSelect(inlineMakeEl, makesCache, 'id', 'name'); inlineMakeEl.disabled = false }
    }

    // ── If logged in, fetch account bike ─────────────────
    if (window.YMM_CUSTOMER_ID) {
      try {
        const r = await get('/me/bike')
        if (r && r.bike && !currentBike) {
          const b = r.bike
          currentBike = {
            year_id: b.ids.year_id, make_id: b.ids.make_id, model_id: b.ids.model_id,
            year: b.year, make: b.make, model: b.model,
          }
          setCookie(COOKIE_NAME, JSON.stringify(currentBike), COOKIE_DAYS)
          updateActiveLabel(currentBike)
          if (currentLabel) currentLabel.textContent = b.year + ' ' + b.make + ' ' + b.model
        }
      } catch {}
    }

    // Render badges already on the page
    if (currentBike) {
      document.querySelectorAll('[data-ymm-badge]').forEach((el) => {
        const lid = el.getAttribute('data-listing-id')
        if (lid) renderBadge(el, lid)
      })
    }

    // Pre-fill selects from saved bike
    if (makeEl && currentBike) await prefillSelects(currentBike, makeEl, modelEl, yearEl)

    // Pre-fill desktop inline selects from saved bike
    if (inlineMakeEl && currentBike) {
      await prefillSelects(currentBike, inlineMakeEl, inlineModelEl, inlineYearEl)
      if (inlineFindBtn) {
        inlineFindBtn.disabled = !(inlineMakeEl.value && inlineModelEl.value && inlineYearEl.value)
      }
    }

    // ── Make / Model / Year change handlers ───────────────
    if (makeEl) {
      makeEl.addEventListener('change', async function () {
        setFieldHasValue(makeEl, !!makeEl.value)
        modelEl.value = ''; yearEl.value = ''; yearEl.disabled = true
        fillSelect(modelEl, [], 'id', 'name'); fillSelect(yearEl, [], 'id', 'year')
        setFieldHasValue(modelEl, false); setFieldHasValue(yearEl, false)
        const makeId = makeEl.value
        if (!makeId) { modelEl.disabled = true; return }
        modelEl.disabled = false
        try {
          let models = modelsCacheByMakeId.get(makeId)
          if (!models) {
            const r = await get('/models', { make_id: makeId })
            models  = (r && r.models) || []
            modelsCacheByMakeId.set(makeId, models)
          }
          fillSelect(modelEl, models, 'id', 'name')
        } catch {}
      })

      modelEl.addEventListener('change', async function () {
        setFieldHasValue(modelEl, !!modelEl.value)
        yearEl.value = ''; yearEl.disabled = true
        fillSelect(yearEl, [], 'id', 'year'); setFieldHasValue(yearEl, false)
        const modelId = modelEl.value
        if (!modelId) return
        try {
          let years = yearsCacheByModelId.get(modelId)
          if (!years) {
            const r = await get('/years', { model_id: modelId })
            years   = (r && r.years) || []
            yearsCacheByModelId.set(modelId, years)
          }
          fillSelect(yearEl, years, 'id', 'year')
          yearEl.disabled = false
        } catch {}
      })

      yearEl.addEventListener('change', async function () {
        setFieldHasValue(yearEl, !!yearEl.value)
        if (makeEl.value && modelEl.value && yearEl.value)
          await onAllThreeFilled(makeEl, modelEl, yearEl, currentLabel)
      })

      if (searchBtn) {
        searchBtn.addEventListener('click', async function () {
          if (makeEl.value && modelEl.value && yearEl.value)
            await onAllThreeFilled(makeEl, modelEl, yearEl, currentLabel)
        })
      }
    }

    // ── Desktop inline Make / Model / Year cascade ────────
    if (inlineMakeEl && inlineModelEl && inlineYearEl) {
      inlineMakeEl.addEventListener('change', async function () {
        setFieldHasValue(inlineMakeEl, !!inlineMakeEl.value)
        inlineModelEl.value = ''; inlineYearEl.value = ''
        inlineModelEl.disabled = true; inlineYearEl.disabled = true
        if (inlineFindBtn) inlineFindBtn.disabled = true
        fillSelect(inlineModelEl, [], 'id', 'name'); fillSelect(inlineYearEl, [], 'id', 'year')
        setFieldHasValue(inlineModelEl, false); setFieldHasValue(inlineYearEl, false)
        const makeId = inlineMakeEl.value
        if (!makeId) return
        try {
          let models = modelsCacheByMakeId.get(makeId)
          if (!models) {
            const r = await get('/models', { make_id: makeId })
            models  = (r && r.models) || []
            modelsCacheByMakeId.set(makeId, models)
          }
          fillSelect(inlineModelEl, models, 'id', 'name')
          inlineModelEl.disabled = false
        } catch {}
      })

      inlineModelEl.addEventListener('change', async function () {
        setFieldHasValue(inlineModelEl, !!inlineModelEl.value)
        inlineYearEl.value = ''; inlineYearEl.disabled = true
        if (inlineFindBtn) inlineFindBtn.disabled = true
        fillSelect(inlineYearEl, [], 'id', 'year'); setFieldHasValue(inlineYearEl, false)
        const modelId = inlineModelEl.value
        if (!modelId) return
        try {
          let years = yearsCacheByModelId.get(modelId)
          if (!years) {
            const r = await get('/years', { model_id: modelId })
            years   = (r && r.years) || []
            yearsCacheByModelId.set(modelId, years)
          }
          fillSelect(inlineYearEl, years, 'id', 'year')
          inlineYearEl.disabled = false
        } catch {}
      })

      inlineYearEl.addEventListener('change', function () {
        setFieldHasValue(inlineYearEl, !!inlineYearEl.value)
        if (inlineFindBtn) {
          inlineFindBtn.disabled = !(inlineMakeEl.value && inlineModelEl.value && inlineYearEl.value)
        }
      })

      if (inlineFindBtn) {
        inlineFindBtn.addEventListener('click', async function () {
          if (inlineMakeEl.value && inlineModelEl.value && inlineYearEl.value)
            await onAllThreeFilled(inlineMakeEl, inlineModelEl, inlineYearEl, currentLabel)
        })
      }
    }

    // ── Garage initialise ────────────────────────────────
    // Seed immediately from cookie so the label / panel shows without waiting for server.
    if (currentBike) {
      garageBikes = [currentBike]
      renderGaragePanel(garageBikes)
    }

    if (window.YMM_CUSTOMER_ID) {
      loadGarageFromServer(true)
    } else {
      let anonGarage = loadGarageFromStorage()
      if (anonGarage.length === 0 && currentBike) {
        anonGarage = [currentBike]
        saveGarageToStorage(anonGarage)
      }
      garageBikes = anonGarage
      renderGaragePanel(garageBikes)
    }
  }

  // ── Public API ───────────────────────────────────────────

  window.YMM = {
    init,
    renderBadge,
    openGapForm,
    openPanel,
    closePanel,
    getCurrentBike: function () { return currentBike },
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})(window, document)
